// The "Shop Metrics" summary table shared by the shelf-test and ecommerce question types.
// Both render the same nine cart/attention metrics from the same answer payload; ecommerce
// then appends its banner and Subscribe & Save sections on top (see its shelf-table-utils).
//
// Ported from the two near-identical calcItemsListProps implementations in
// shelf_test/shelf/items_list.coffee and ecommerce/shelf/items_list.coffee. Neither used
// anything from the ItemsListView base they extended — no sorting, no clickable labels — so
// the callers build plain { cols, rows }.

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'

// Coffee wrote `Math.round(v * 100, 2) / 100`; the second argument does nothing in JS, so
// this is a plain 2-decimal round. A missing metric yields NaN, which `|| 0` absorbs.
export const round2 = value => Math.round(value * 100) / 100 || 0

export const seconds = value => `${round2(value)}s`

export const percent = value => `${((value || 0) * 100).toFixed(1)}%`

export const metricRow = (title, value, position, loading) => ({
  cells: [{ title }, { value, loading }],
  position
})

export const headerRow = (title, position) => ({
  cells: [{ title, header: true }, {}],
  position
})

// The payload has shipped a misspelled key since launch; alias rather than depend on it.
export const readAverageAttention = values =>
  Object.hasOwn(values, 'averageAttension') ? values.averageAttension : values.averageAttention

export const hasBaseValues = values =>
  Object.hasOwn(values, 'base') ||
  Object.keys(values).some(key => values[key] != undefined && Object.hasOwn(values[key], 'base'))

const valueColTitle = ({ answer, hasBase, hideTotal, viewMode }) => {
  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`
  if (hideTotal) return 'N'

  const total = formatAmounts(answer?.total || 0)
  return `N<small>${'amount' === viewMode ? `(${total} total)` : `(from ${total} resp.)`}</small>`
}

export const buildShelfMetricsCols = ({ values, viewMode, hideTotal }) => [
  { type: 'title', title: 'Description' },
  { type: 'value', title: valueColTitle({ answer: values, hasBase: hasBaseValues(values), hideTotal, viewMode }) }
]

// The nine metrics both question types share, in their shipped order (positions 1-9).
export const buildShelfMetricsRows = ({ values, isLoading, showPrice, priceMask }) => {
  const price = showPrice ? priceMask.replace('{{price}}', round2(values.averageCartSpend)) : 'N/A'
  const totalPrice = showPrice ? priceMask.replace('{{price}}', round2(values.totalProductSpend)) : 'N/A'

  return [
    metricRow('Average cart spend', price, 1, isLoading),
    metricRow('Average cart size', round2(values.averageCartSize), 2, isLoading),
    metricRow('Average number of unique products in cart', round2(values.averageUniqueProductsInCart), 3, isLoading),
    metricRow('Total products purchased', round2(values.totalProductsPurchased), 4, isLoading),
    metricRow('Total products picked up', round2(values.totalProductsPickedUp), 5, isLoading),
    metricRow('Total product spend', totalPrice, 6, isLoading),
    metricRow('Average time at shelf', seconds(values.averageTimeAtShelf), 7, isLoading),
    metricRow('Average time to first click', seconds(values.averageTimeToFirstClick), 8, isLoading),
    metricRow('Average attention span per product', seconds(readAverageAttention(values)), 9, isLoading)
  ]
}
